import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Loading } from "@/components/Common";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import DailyEntry from "@/pages/DailyEntry";
import Ledger from "@/pages/Ledger";
import SalesOrders from "@/pages/SalesOrders";
import PurchaseOrders from "@/pages/PurchaseOrders";
import PoReceipts from "@/pages/po/PoReceipts";
import PoDetail from "@/pages/po/PoDetail";
import Vendors from "@/pages/Vendors";
import ReorderPlan from "@/pages/ReorderPlan";
import SalesAnalysis from "@/pages/SalesAnalysis";
import DeadStock from "@/pages/DeadStock";
import Settings from "@/pages/Settings";
import UsersPage from "@/pages/Users";
import PrintPO from "@/pages/PrintPO";
import AsinCatalogue from "@/pages/AsinCatalogue";
import Shipments from "@/pages/Shipments";
import ShipmentUpload from "@/pages/ShipmentUpload";
import ShipmentActivity from "@/pages/ShipmentActivity";
import CourierSettings from "@/pages/CourierSettings";
import DataSafety from "@/pages/DataSafety";
import ReturnsDashboard from "@/pages/returns/Dashboard";
import ReceiveReturn from "@/pages/returns/Receive";
import ActionQueue from "@/pages/returns/Queue";
import Claims from "@/pages/returns/Claims";
import ReturnSettings from "@/pages/returns/Settings";
import { ReturnsRegister, ReturnDetail } from "@/pages/returns/Register";

const HOME = [["stock", "/"], ["shipments", "/shipments"], ["entries", "/daily-entry"], ["orders", "/sales-orders"]];

const NoAccess = () => (
  <div className="py-20 text-center text-sm text-muted-foreground" data-testid="no-access">You don't have access to any section yet. Ask the admin to give you permission.</div>
);

const Protected = ({ children, admin, section, level = "view" }) => {
  const { user, isAdmin, can, prefs } = useAuth();
  if (user === null || (user && prefs === null)) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !isAdmin) return <Navigate to="/" replace />;
  if (section && !can(section, level)) {
    const home = HOME.find(([s]) => can(s));
    return home && home[1] !== "/" ? <Navigate to={home[1]} replace /> : <NoAccess />;
  }
  return children;
};

const S = (section, el, level) => <Protected section={section} level={level}>{el}</Protected>;

const LoginRoute = () => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? <Navigate to="/" replace /> : <Login />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/print/po/:id" element={<Protected section="orders"><PrintPO /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route index element={S("stock", <Dashboard />)} />
              <Route path="products" element={S("stock", <Products />)} />
              <Route path="daily-entry" element={S("entries", <DailyEntry />)} />
              <Route path="ledger" element={S("stock", <Ledger />)} />
              <Route path="asins" element={S("stock", <AsinCatalogue />)} />
              <Route path="sales-orders" element={S("orders", <SalesOrders />)} />
              <Route path="purchase-orders" element={S("orders", <PurchaseOrders />)} />
              <Route path="po-receipts" element={S("orders", <PoReceipts />)} />
              <Route path="po-receipts/po/:id" element={S("orders", <PoDetail />)} />
              <Route path="reorder" element={S("stock", <ReorderPlan />)} />
              <Route path="sales-analysis" element={S("stock", <SalesAnalysis />)} />
              <Route path="dead-stock" element={S("stock", <DeadStock />)} />
              <Route path="settings" element={S("stock", <Settings />)} />
              <Route path="shipments" element={S("shipments", <Shipments />)} />
              <Route path="shipments/upload" element={S("shipment_uploads", <ShipmentUpload />, "edit")} />
              <Route path="shipments/activity" element={S("shipments", <ShipmentActivity />)} />
              <Route path="shipments/couriers" element={<Protected admin><CourierSettings /></Protected>} />
              <Route path="returns" element={S("returns", <ReturnsDashboard />)} />
              <Route path="returns/receive" element={S("returns", <ReceiveReturn />, "edit")} />
              <Route path="returns/register" element={S("returns", <ReturnsRegister />)} />
              <Route path="returns/queue" element={S("returns", <ActionQueue />)} />
              <Route path="returns/claims" element={S("returns", <Claims />)} />
              <Route path="returns/settings" element={S("returns_settings", <ReturnSettings />, "edit")} />
              <Route path="returns/new" element={S("returns", <ReturnDetail />, "edit")} />
              <Route path="returns/r/:id" element={S("returns", <ReturnDetail />)} />
              <Route path="data-safety" element={<Protected admin><DataSafety /></Protected>} />
              <Route path="users" element={<Protected admin><UsersPage /></Protected>} />
              <Route path="vendors" element={<Protected admin><Vendors /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
